import { useState, useContext, useEffect } from 'react';
import { GlobalContext } from 'GlobalContext';
import { message, Spin, Tooltip, Upload, Popconfirm, InputNumber } from 'antd';
import api from 'api';
import { get } from 'utils/fetch';
import { isPhoneSize } from 'utils/util';
import {
    FileOutlined,
    CloseCircleFilled,
    PlusOutlined,
} from '@ant-design/icons';
import { USDollar } from 'utils/format';

export function AccountingInfo() {
    const { manageAssetId, setManageAssetId, accessToken, refreshSession } =
        useContext(GlobalContext);
    const [messageApi, contextHolder] = message.useMessage();
    const [propertyAccountingInfo, setPropertyAccountingInfo] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [loading, setLoading] = useState(true);

    // fields
    const [cashInvested, setCashInvested] = useState(null);
    const [costOfCapital, setCostOfCapital] = useState(null);
    const [totalCostExcludeCapital, setTotalCostExcludeCapital] =
        useState(null);
    const [estimateProfit, setEstimateProfit] = useState(null);
    const [alphaxProfit, setAlphaxProfit] = useState(null);
    const [plLink, setPlLink] = useState(null);

    const entryStyle = {
        margin: '20px',
        fontSize: '20px',
    };

    const inputStyle = {
        border: '0',
        outline: 'none',
        borderBottom: '1px solid black',
        fontSize: '20px',
        background: 'transparent',
    };

    const displayErrorMessage = (message) => {
        messageApi.open({
            type: 'error',
            content: message,
        });
    };

    async function fetchPropertyAccountingInfo(manageAssetId) {
        await get(
            api.asset.propertyAccountingInfo(manageAssetId),
            setPropertyAccountingInfo,
            (errors) =>
                errors.forEach((err) => {
                    displayErrorMessage(err.message);
                }),
            accessToken
        );
        refreshSession();
        setLoading(false);
    }

    useEffect(() => {
        if (propertyAccountingInfo) {
            restore(propertyAccountingInfo);
        }
    }, [propertyAccountingInfo]);

    useEffect(() => {
        if (manageAssetId < 0) {
            return;
        }
        fetchPropertyAccountingInfo(manageAssetId);
    }, []);

    function restore(propertyAccountingInfo) {
        setCashInvested(propertyAccountingInfo?.cash_invested);
        setCostOfCapital(propertyAccountingInfo?.cost_of_capital);
        setTotalCostExcludeCapital(
            propertyAccountingInfo?.total_cost_exclude_capital
        );
        setEstimateProfit(propertyAccountingInfo?.estimate_profit);
        setAlphaxProfit(propertyAccountingInfo?.alphax_profit);
        setPlLink(propertyAccountingInfo?.pl_link);
    }

    function unchanged() {
        if (!propertyAccountingInfo) return false;
        return (
            cashInvested === propertyAccountingInfo.cash_invested &&
            costOfCapital === propertyAccountingInfo.cost_of_capital &&
            totalCostExcludeCapital ===
                propertyAccountingInfo.total_cost_exclude_capital &&
            estimateProfit === propertyAccountingInfo.estimate_profit &&
            alphaxProfit === propertyAccountingInfo.alphax_profit &&
            plLink === propertyAccountingInfo.pl_link
        );
    }

    async function upsert() {
        if (unchanged()) return;
        setLoading(true);
        const body = JSON.stringify({
            cashInvested,
            costOfCapital,
            totalCostExcludeCapital,
            estimateProfit,
            alphaxProfit,
            plLink,
        });

        const options = {
            method: 'POST', // *GET, POST, PUT, DELETE, etc.
            mode: 'cors', // no-cors, *cors, same-origin
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
            },
            body,
        };
        const response = await fetch(
            api.asset.upsertAccounting(manageAssetId),
            options
        );
        const data = await response.json();
        const idRes = data.data;
        refreshSession();
        if (idRes === 0) {
            messageApi.error({
                type: 'error', // or 'error', 'info', 'warning', 'loading'
                content: propertyAccountingInfo?.id
                    ? 'Accounting insertion failed'
                    : 'Accounting update failed',
                duration: 3, // seconds (optional)
            });
        } else {
            messageApi.open({
                type: 'success', // or 'error', 'info', 'warning', 'loading'
                content: propertyAccountingInfo?.id
                    ? 'Accounting insertion success'
                    : 'Accounting update success',
                duration: 3, // seconds (optional)
            });
            fetchPropertyAccountingInfo(manageAssetId);
        }
        setEditMode(false);
    }

    async function deleteAccounting() {
        setLoading(true);

        const options = {
            method: 'DELETE',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
            },
        };

        const response = await fetch(
            api.asset.deleteAccounting(manageAssetId),
            options
        );
        const data = await response.json();
        const idRes = data.data;
        refreshSession();

        if (idRes === 0 || !idRes) {
            messageApi.error({
                type: 'error',
                content: 'Accounting deletion failed',
                duration: 3,
            });
        } else {
            messageApi.open({
                type: 'success',
                content: 'Accounting deletion success',
                duration: 3,
            });
        }
        setPropertyAccountingInfo(null);
        restore(null);
        setLoading(false);
    }

    return (
        <>
            {contextHolder}
            {loading && (
                <div style={{ marginTop: '30px', textAlign: 'center' }}>
                    <Spin />
                </div>
            )}
            {!loading && (
                <div>
                    <div
                        style={{
                            width: 'fit-content',
                            marginLeft: '20px',
                            height: '38px',
                            position: 'fixed',
                            background: 'white',
                            width: '100%',
                            alignContent: 'end',
                            top: 40,
                        }}
                    >
                        <div
                            style={{
                                color: 'blue',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                width: 'fit-content',
                            }}
                            onClick={(e) => {
                                if (editMode) {
                                    restore(propertyAccountingInfo);
                                }
                                setEditMode(!editMode);
                            }}
                        >
                            {editMode ? 'Restore' : 'Edit'}
                        </div>
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : {
                                      ...entryStyle,
                                      display: 'flex',
                                      marginTop: '50px',
                                  }
                        }
                    >
                        <div>Cash Invested: </div>
                        {editMode ? (
                            <InputNumber
                                style={{
                                    outline: 'none',
                                    border: 'none',
                                    borderBottom: '1px solid black',
                                    width: 'auto', // optional, can adjust
                                }}
                                value={cashInvested}
                                onChange={(value) => setCashInvested(value)} // value is already numeric or null
                                placeholder="cash invested"
                                controls={false} // hides up/down arrows
                            />
                        ) : (
                            <div>{USDollar.format(cashInvested || 0)}</div>
                        )}
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : {
                                      ...entryStyle,
                                      display: 'flex',
                                  }
                        }
                    >
                        <div>Cost of Capital: </div>
                        {editMode ? (
                            <InputNumber
                                style={{
                                    outline: 'none',
                                    border: 'none',
                                    borderBottom: '1px solid black',
                                    width: 'auto', // optional, can adjust
                                }}
                                value={costOfCapital}
                                onChange={(value) => setCostOfCapital(value)} // value is already numeric or null
                                placeholder="cost of capital"
                                controls={false} // hides up/down arrows
                            />
                        ) : (
                            <div>{USDollar.format(costOfCapital || 0)}</div>
                        )}
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : {
                                      ...entryStyle,
                                      display: 'flex',
                                  }
                        }
                    >
                        <div>Total Cost (excluding cost of capital): </div>
                        {editMode ? (
                            <InputNumber
                                style={{
                                    outline: 'none',
                                    border: 'none',
                                    borderBottom: '1px solid black',
                                    width: 'auto', // optional, can adjust
                                }}
                                value={totalCostExcludeCapital}
                                onChange={(value) =>
                                    setTotalCostExcludeCapital(value)
                                } // value is already numeric or null
                                placeholder="total cost excluding capital"
                                controls={false} // hides up/down arrows
                            />
                        ) : (
                            <div>
                                {USDollar.format(totalCostExcludeCapital || 0)}
                            </div>
                        )}
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : {
                                      ...entryStyle,
                                      display: 'flex',
                                  }
                        }
                    >
                        <div>Estimate Profit: </div>
                        {editMode ? (
                            <InputNumber
                                style={{
                                    outline: 'none',
                                    border: 'none',
                                    borderBottom: '1px solid black',
                                    width: 'auto', // optional, can adjust
                                }}
                                value={estimateProfit}
                                onChange={(value) => setEstimateProfit(value)} // value is already numeric or null
                                placeholder="estimate profit"
                                controls={false} // hides up/down arrows
                            />
                        ) : (
                            <div>{USDollar.format(estimateProfit || 0)}</div>
                        )}
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : {
                                      ...entryStyle,
                                      display: 'flex',
                                  }
                        }
                    >
                        <div>AlphaX Profit: </div>
                        {editMode ? (
                            <InputNumber
                                style={{
                                    outline: 'none',
                                    border: 'none',
                                    borderBottom: '1px solid black',
                                    width: 'auto', // optional, can adjust
                                }}
                                value={alphaxProfit}
                                onChange={(value) => setAlphaxProfit(value)} // value is already numeric or null
                                placeholder="alphax profit"
                                controls={false} // hides up/down arrows
                            />
                        ) : (
                            <div>{USDollar.format(alphaxProfit || 0)}</div>
                        )}
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : {
                                      ...entryStyle,
                                      display: 'flex',
                                  }
                        }
                    >
                        {editMode && (
                            <>
                                <div>PL Link: </div>
                                <input
                                    style={inputStyle}
                                    value={plLink || ''}
                                    onInput={(e) => {
                                        setPlLink(e.target.value);
                                    }}
                                ></input>
                            </>
                        )}

                        {!editMode && `PL Link: ${plLink || ''}`}
                    </div>

                    <div style={{ display: 'flex', marginLeft: '20px' }}>
                        <button
                            style={{
                                height: '30px',
                                width: '60px',
                                borderRadius: '30px',
                                background: 'rgb(17 32 86)',
                                color: 'white',
                                cursor: 'pointer',
                            }}
                            onClick={upsert}
                        >
                            Save
                        </button>
                        <button
                            style={{
                                height: '30px',
                                width: '60px',
                                borderRadius: '30px',
                                background: 'rgb(86 17 17)',
                                color: 'white',
                                cursor: 'pointer',
                            }}
                            onClick={() => {
                                if (
                                    window.confirm(
                                        'Are you sure you want to delete this loan?'
                                    )
                                ) {
                                    deleteAccounting();
                                }
                            }}
                        >
                            Delete
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
